import { useContext } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { ThemeContext } from '@/components/theme-provider'
import { useLocalStorage, useDarkMode, useReadLocalStorage } from 'usehooks-ts'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'

export default function ThemeSwitcher() {
  const { t } = useTranslations()
  const [theme, setThemeValue, removeThemeValue] = useLocalStorage<'light' | 'dark' | null>('theme', null)
  const { isDarkMode } = useDarkMode()

  const { currentTheme } = useContext(ThemeContext)

  const themeButtonStyle =
    'block hover:bg-secondary-50 w-full py-2 px-4 rounded-lg text-base mb-1 text-primaryBlue hover:dark:text-secondary-800'

  const enableDarkMode = useReadLocalStorage('bwDarkMode')

  return (
    <div>
      <Menu>
        <MenuButton
          title={t('Theme')}
          className={cx(
            "h-10 w-10 border-secondary-200 dark:border-secondary-500 transition-colors rounded-lg relative flex items-center justify-center outline-none",
            'text-indigoBlue hover:text-primaryBlue dark:text-white dark:hover:text-primaryBlue!',
          )}
        >
          {currentTheme === 'light' ? (
            <svg
              className="h-8 w-8 fill-none!"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
            >
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" /> <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /> <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" /> <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <div className="absolute bottom-[11px] right-[10px]">
              <span className="block w-[25px] h-[25px] rounded-[50%] dark:shadow-[3px_3px_0_0_currentColor]" />
            </div>
          )}
        </MenuButton>
        <Transition
          enter="transition ease-out duration-75"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <MenuItems anchor="top end" className="rounded-lg bg-secondary-100 p-2 w-32 outline-none z-20">
            <MenuItem>
              <button
                type="button"
                className={cx(themeButtonStyle, theme === 'light' && 'bg-secondary-50')}
                onClick={() => {
                  setThemeValue('light')
                }}
              >
                {t('Light')}
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                className={cx(themeButtonStyle, theme === 'dark' && 'bg-secondary-50 dark:text-secondary-800')}
                onClick={() => {
                  setThemeValue('dark')
                }}
              >
                {t('Dark')}
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                className={cx(
                  themeButtonStyle,
                  isDarkMode && theme === null && 'bg-secondary-50 dark:text-secondary-800',
                  !isDarkMode && theme === null && 'bg-secondary-50'
                )}
                onClick={() => {
                  removeThemeValue()
                }}
              >
                {t('System')}
              </button>
            </MenuItem>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}
